import dayjs from '@disco/utils/dayjs'

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  changeMode,
  loadStats,
  loadSummaryStats,
} from '@disco/state-management/actions/StatsActions'
import { getTrialStartDate } from '@disco/state-management/selectors/user'
import StatsDateRange from 'components/common/stats-date-range'
import TrackName from 'components/common/track-name'
import UserName from 'components/common/user-name'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import PageContainer from 'components/ui/page-container'
import InsightsLimitedAlert from '../InsightsLimitedAlert'
import InsightsTrialAlert from '../InsightsTrialAlert'
import Summary from './summary'
import styles from './InsightsTeam.module.css'

type Props = {
  isFeatureEnabled?: boolean
  isFeatureLimited?: boolean
  isTrial: boolean
  state: State['stats']
}
export const InsightsTeam = ({
  isFeatureEnabled,
  isFeatureLimited,
  isTrial,
  state,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const {
    endDate,
    entities: { tracks, users },
    startDate,
    summary,
  } = state
  const trialStartDate: Date = useSelector(getTrialStartDate)
  useEffect(() => {
    dispatch(changeMode('team'))
  }, [])
  useEffect(() => {
    if (isFeatureEnabled) {
      dispatch(sendMixpanelEvent('Open team analytics'))
    }
  }, [isFeatureEnabled])
  useEffect(() => {
    dispatch(loadSummaryStats())
    dispatch(loadStats('tracks', 'playbacks'))
    dispatch(loadStats('tracks', 'pitches'))
    dispatch(loadStats('users', 'playlists'))
  }, [endDate, startDate])

  const renderAlert = () => {
    if (isTrial && isFeatureEnabled) {
      return <InsightsTrialAlert />
    }

    if (isFeatureLimited) {
      return <InsightsLimitedAlert />
    }

    return null
  }

  const renderSummary = () => {
    const isAllTime =
      dayjs(startDate).isSame(dayjs(trialStartDate), 'day') &&
      dayjs(endDate).isSame(dayjs(), 'day')
    return (
      summary.data && (
        <div className={styles.insightsTeamSummary}>
          <Summary
            disabled={!isFeatureEnabled && !isFeatureLimited}
            loading={summary.loading}
            showPercentage={!isAllTime}
            {...summary}
          />
        </div>
      )
    )
  }

  return (
    <PageContainer>
      <PageContainer.Section
        title="Team analytics"
        action={
          <StatsDateRange disabled={!isFeatureEnabled && !isFeatureLimited} />
        }
      >
        <DocumentHeadTags title="Team analytics | DISCO" />
        {renderSummary()}
        {renderAlert()}
      </PageContainer.Section>
      <PageContainer.Section
        title="Most played tracks and videos"
        action={
          <Button
            appearance="inline-link"
            caption="View more"
            disabled={!isFeatureEnabled || tracks.playbacks.data.length <= 5}
            link="/insights/team/tracks-playbacks"
          />
        }
      >
        <DataTable
          appearance="plain"
          columns={[
            {
              name: 'File',
              render: ({ stats, ...track }: Record<string, any>) => (
                <TrackName track={track as any} />
              ),
            },
            {
              name: 'Artist',
              render: (item: Record<string, any>) => item.artist,
            },
            {
              name: 'Played',
              width: 'content',
              render: (item: Record<string, any>) => item.stats.play_count,
            },
          ]}
          data={tracks.playbacks.data.slice(0, 5)}
          loading={tracks.playbacks.loading}
          rows={5}
        />
      </PageContainer.Section>
      <PageContainer.Section
        title="Most pitched files"
        action={
          <Button
            appearance="inline-link"
            caption="View more"
            disabled={!isFeatureEnabled || tracks.pitches.data.length <= 5}
            link="/insights/team/tracks-pitches"
          />
        }
      >
        <DataTable
          appearance="plain"
          columns={[
            {
              name: 'File',
              render: ({ stats, ...track }: Record<string, any>) => (
                <TrackName track={track as any} />
              ),
            },
            {
              name: 'Artist',
              render: (item: Record<string, any>) => item.artist,
            },
            {
              name: 'Pitched',
              width: 'content',
              render: (item: Record<string, any>) => item.stats.pitch_count,
            },
          ]}
          data={tracks.pitches.data.slice(0, 5)}
          loading={tracks.pitches.loading}
          rows={5}
        />
      </PageContainer.Section>
      <PageContainer.Section
        title="Most playlists created by team member"
        action={
          <Button
            appearance="inline-link"
            caption="View more"
            disabled={!isFeatureEnabled || users.playlists.data.length <= 5}
            link="/insights/team/users-playlists"
          />
        }
      >
        <DataTable
          appearance="plain"
          columns={[
            {
              name: 'Name',
              render: ({ email, first_name, last_name }) => (
                <UserName
                  user={{
                    email,
                    first_name,
                    last_name,
                  }}
                />
              ),
            },
            {
              name: 'Created',
              width: 'content',
              render: (item: Record<string, any>) =>
                item.stats.playlist_versions_owned,
            },
          ]}
          data={users.playlists.data.slice(0, 5)}
          loading={users.playlists.loading}
          rows={5}
        />
      </PageContainer.Section>
    </PageContainer>
  )
}
export default React.memo<Props>(InsightsTeam)
